import { RequestMethod } from "../../utils/http/index.js";
import GlobalController from "../base-controllers/GlobalController.js";
import ActionSubmission from "../element-controllers/action/action_submission.js";
import { getAttr } from "../utils/dom.js";
import { clearCache } from "../utils/turbo.js";
import { SSE_CHANNEL_EVENT, SSE_RECONNECTED_EVENT } from "./EventStream.js";
export function furnitureRefreshURL(href) {
    const url = new URL(href);
    // Navigation may have forced HTML; the background request needs fragments.
    url.searchParams.delete("format");
    return url;
}
/** Re-read idle furniture views without merging another user's edits. */
class FurnitureUpdates extends GlobalController {
    constructor() {
        super(...arguments);
        this.indicatorEnabled = false;
        this.pending = false;
        this.failed = false;
        this.wasBlocked = false;
        this.received = (event) => {
            const channel = event.detail;
            const prefix = "furniture:changed:";
            if (typeof channel !== "string" || !channel.startsWith(prefix))
                return;
            clearCache();
            if (!this.focusOnly && (this.view === "*" || this.view === channel.slice(prefix.length)))
                this.catchUp();
        };
        this.reconnected = () => {
            if (this.focusOnly)
                clearCache();
            else
                this.catchUp();
        };
        this.visible = () => {
            if (document.visibilityState === "visible")
                this.catchUp();
        };
        this.catchUp = () => {
            if (!this.view)
                return;
            this.pending = true;
            this.failed = false;
            this.wasBlocked = this.blocked();
            this.schedule();
        };
    }
    init() {
        window.addEventListener(SSE_CHANNEL_EVENT, this.received);
        window.addEventListener(SSE_RECONNECTED_EVENT, this.reconnected);
        window.addEventListener("focus", this.catchUp);
        document.addEventListener("visibilitychange", this.visible);
        this.observer = new MutationObserver(() => {
            const blocked = this.blocked();
            if (this.wasBlocked && !blocked)
                this.schedule();
            this.wasBlocked = blocked;
        });
        this.observer.observe(document.body, {
            subtree: true, childList: true, attributes: true,
            attributeFilter: ["open", "aria-busy", "data-changed"],
        });
    }
    destroy() {
        this.onNavigation();
        this.observer?.disconnect();
        window.removeEventListener(SSE_CHANNEL_EVENT, this.received);
        window.removeEventListener(SSE_RECONNECTED_EVENT, this.reconnected);
        window.removeEventListener("focus", this.catchUp);
        document.removeEventListener("visibilitychange", this.visible);
    }
    onNavigation() {
        this.pending = false;
        this.failed = false;
        clearTimeout(this.timer);
        this.timer = undefined;
        this.submission?.cancel();
        this.submission = undefined;
        this.requestUrl = undefined;
    }
    get view() {
        return document.getElementById("main-content")?.dataset.furnitureUpdates || undefined;
    }
    get focusOnly() {
        return document.getElementById("main-content")?.dataset.furnitureUpdateMode === "focus";
    }
    blocked() {
        if (window.hasPageUpdatesPromise || document.querySelector("dialog[open]"))
            return true;
        return [...document.querySelectorAll('form[data-controller~="Form"]')].some(form => {
            if (form.getAttribute("aria-busy") === "true")
                return true;
            if (!form.getClientRects().length)
                return false;
            if (getAttr(form, "data-list-filter", Boolean) && form.method.toLowerCase() === "get") {
                // Bound GET criteria are marked changed by the server, even when idle.
                // Do still protect a query being typed before its debounced submission.
                const active = document.activeElement;
                return active instanceof HTMLInputElement && form.contains(active) && active.value !== active.defaultValue;
            }
            if (form.hasAttribute("data-changed"))
                return true;
            return form.dataset.warnIfChanged !== "false" || form.contains(document.activeElement);
        });
    }
    schedule() {
        if (!this.pending || this.failed || !this.view || this.submission || this.timer !== undefined)
            return;
        if (document.visibilityState !== "visible" || this.blocked())
            return;
        this.timer = window.setTimeout(() => {
            this.timer = undefined;
            void this.refresh();
        }, 150);
    }
    async refresh() {
        if (!this.view || this.blocked() || document.visibilityState !== "visible")
            return;
        this.pending = false;
        this.requestUrl = location.href;
        const submission = new ActionSubmission(this, RequestMethod.get, furnitureRefreshURL(this.requestUrl), {
            extraHeaders: {
                "Page-Updates": JSON.stringify([{ action: "morph", target_id: "main-content" }]),
            },
        });
        this.submission = submission;
        try {
            await submission.perform();
        }
        catch {
            // Keep the stale flag; reconnect/focus/the next signal will try again.
            this.requestErrored();
        }
        finally {
            if (this.submission === submission) {
                this.submission = undefined;
                this.schedule();
            }
        }
    }
    requestShouldHandleResponse(_request, response) {
        if (location.href === this.requestUrl && this.view && !this.blocked() && document.visibilityState === "visible" && response.isFragments)
            return true;
        // Gate before fragment dispatch, not through browser-dependent listener ordering.
        this.pending = location.href === this.requestUrl;
        this.failed = !response.isFragments;
        this.wasBlocked = this.blocked();
        return false;
    }
    async requestErrored() {
        this.pending = true;
        this.failed = true;
    }
    async requestFailedWithResponse() {
        this.requestErrored();
    }
}
FurnitureUpdates.id = "FurnitureUpdates";
export default FurnitureUpdates;
//# sourceMappingURL=FurnitureUpdates.js.map